import {
  detectRosterTable,
  parseRosterTable,
  parseRosterTextForClass,
  RosterParseError,
  type RosterRow,
  rosterTableClasses,
  type Student,
  studentRosterKey,
  suggestRosterClass,
} from "@classroom/domain";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "./i18n";
import { RosterFileError, type RosterSheet, readRosterCsv, readRosterWorkbook } from "./lib/rosterImport";
import SelectMenu from "./SelectMenu";
import "./roster-excel-import.css";

export type RosterImportMethod = "excel" | "csv";

export default function RosterImportPanel({
  className,
  students,
  readOnly,
  initialMethod = "excel",
  onImport,
}: {
  className: string;
  students: Student[];
  readOnly: boolean;
  initialMethod?: RosterImportMethod;
  onImport: (rows: RosterRow[]) => Promise<void>;
}) {
  const { t } = useTranslation("rosterPaste");
  const { t: studentText } = useTranslation("rosterAdmin");
  const input = useRef<HTMLInputElement>(null);
  const generation = useRef(0);
  const [method, setMethod] = useState(initialMethod);
  const [crossClass, setCrossClass] = useState(students.some((student) => student.sourceClass));
  const [sheets, setSheets] = useState<RosterSheet[]>([]);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [text, setText] = useState("");
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const [error, setError] = useState("");
  useEffect(() => setMethod(initialMethod), [initialMethod]);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );
  const sheet = sheets[sheetIndex];
  const hasSource = method === "excel" ? Boolean(sheet) : Boolean(text.trim());
  const dirty = sheets.length > 0 || Boolean(text.trim());

  function errorText(caught: unknown) {
    if (caught instanceof RosterFileError)
      return method === "csv" ? t(`csv.errors.${caught.code}`) : t(`excel.errors.${caught.code}`);
    if (caught instanceof RosterParseError && caught.code === "columns") return t("excel.errors.columns");
    if (caught instanceof RosterParseError && caught.code === "class")
      return crossClass ? t("crossClassMissing") : t("excel.errors.class", { className });
    if (caught instanceof RosterParseError)
      return t(`errors.${caught.code}`, { line: caught.line, number: caught.studentNumber });
    return caught instanceof Error ? caught.message : t("excel.errors.read");
  }

  const review = useMemo(() => {
    if (!hasSource) return { rows: [] as RosterRow[], error: undefined };
    try {
      if (method === "csv") return { rows: parseRosterTextForClass(text, className, crossClass), error: undefined };
      if (!sheet) return { rows: [] as RosterRow[], error: undefined };
      const mapping = detectRosterTable(sheet.rows);
      const selectedClass = suggestRosterClass(rosterTableClasses(sheet.rows, mapping), className);
      return { rows: parseRosterTable(sheet.rows, mapping, crossClass ? null : selectedClass), error: undefined };
    } catch (caught) {
      return { rows: [] as RosterRow[], error: caught };
    }
  }, [hasSource, method, text, sheet, className, crossClass]);
  const updates = review.rows.filter((row) =>
    students.some((student) => studentRosterKey(student) === studentRosterKey(row)),
  ).length;

  async function read(file?: File) {
    if (!file || pending.current || readOnly) return;
    const current = ++generation.current;
    if (method === "excel") setSheets([]);
    else setText("");
    setError("");
    setReading(true);
    try {
      if (method === "csv") {
        const next = await readRosterCsv(file);
        if (generation.current === current) {
          setText(next);
          if (!next.trim()) setError(t("csv.errors.empty"));
        }
      } else {
        const next = await readRosterWorkbook(file);
        if (generation.current !== current) return;
        const readable = (item: RosterSheet) => {
          const guess = detectRosterTable(item.rows);
          return guess.numberColumn >= 0 && guess.nameColumn >= 0;
        };
        const matching = next.findIndex((item) => {
          const guess = detectRosterTable(item.rows);
          return readable(item) && Boolean(suggestRosterClass(rosterTableClasses(item.rows, guess), className));
        });
        setSheets(next);
        setSheetIndex(Math.max(0, matching >= 0 ? matching : next.findIndex(readable)));
      }
    } catch (caught) {
      if (generation.current === current) setError(errorText(caught));
    } finally {
      if (generation.current === current) setReading(false);
    }
  }

  function clear() {
    generation.current += 1;
    if (method === "excel") setSheets([]);
    else setText("");
    setError("");
    setReading(false);
    if (input.current) input.current.value = "";
  }

  async function save() {
    if (readOnly || pending.current || reading || !review.rows.length || review.error) return;
    pending.current = true;
    setSaving(true);
    setError("");
    try {
      await onImport(review.rows);
      clear();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      pending.current = false;
      setSaving(false);
    }
  }

  return (
    <section
      className="roster-import-panel"
      data-has-preview={review.rows.length > 0 || undefined}
      aria-label={t("importTitle")}
      data-save-state={saving || reading ? "saving" : dirty ? "dirty" : "saved"}
      data-autosave={dirty || saving || reading || undefined}
    >
      <fieldset disabled={readOnly || saving || reading}>
        <legend className="sr-only">{t("importTitle")}</legend>
        <fieldset className="roster-import-methods">
          <legend className="sr-only">{t("format")}</legend>
          {(["excel", "csv"] as const).map((value) => (
            <button
              type="button"
              key={value}
              aria-pressed={method === value}
              onClick={() => {
                setMethod(value);
                setError("");
              }}
            >
              {t(value === "excel" ? "excelMethod" : "csvMethod")}
            </button>
          ))}
        </fieldset>
        <label className="roster-cross-class">
          <input
            type="checkbox"
            checked={crossClass}
            onChange={(event) => {
              setCrossClass(event.target.checked);
              setError("");
            }}
          />
          {t("crossClass")}
        </label>
        <label className="roster-excel-file">
          {t(method === "excel" ? "excel.file" : "csv.file")}
          <input
            key={method}
            ref={input}
            type="file"
            accept={
              method === "excel"
                ? ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                : ".csv,text/csv"
            }
            onChange={(event) => void read(event.target.files?.[0])}
          />
        </label>
        {method === "csv" && (
          <label className="roster-csv-text">
            {t("csv.content")}
            <textarea
              data-tour="roster-input"
              aria-label={t("input")}
              value={text}
              onChange={(event) => {
                setText(event.target.value);
                setError("");
              }}
              rows={4}
              placeholder={t("csv.placeholder")}
            />
          </label>
        )}
        {reading && <p role="status">{t(method === "excel" ? "excel.reading" : "csv.reading")}</p>}
        {method === "excel" && sheets.length > 1 && (
          <div className="roster-excel-sheet">
            <span>{t("excel.sheet")}</span>
            <SelectMenu
              label={t("excel.sheet")}
              value={String(sheetIndex)}
              options={sheets.map((item, index) => ({ value: String(index), label: item.name }))}
              onChange={(value) => {
                setSheetIndex(Number(value));
                setError("");
              }}
            />
          </div>
        )}
        {review.error ? (
          <p className="form-error" role="alert">
            {errorText(review.error)}
          </p>
        ) : (
          review.rows.length > 0 && (
            <>
              <p role="status">{t("excel.summary", { className, count: review.rows.length, updates })}</p>
              {/* biome-ignore lint/a11y/noNoninteractiveTabindex: Keyboard focus allows scrolling the roster preview. */}
              <section className="roster-excel-scroll" tabIndex={0} aria-label={t("excel.preview")}>
                <table>
                  <caption>{t("excel.preview")}</caption>
                  <thead>
                    <tr>
                      {crossClass && <th scope="col">{t("sourceClass")}</th>}
                      <th scope="col">{studentText("number")}</th>
                      <th scope="col">{studentText("name")}</th>
                      <th scope="col">{studentText("gender")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {review.rows.map((row) => (
                      <tr key={studentRosterKey(row)}>
                        {crossClass && <td>{row.sourceClass}</td>}
                        <td>{row.number}</td>
                        <td>{row.name}</td>
                        <td>{row.gender ? t(`excel.genders.${row.gender}`) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            </>
          )
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {(hasSource || error) && (
          <div className="roster-excel-actions">
            <button type="button" onClick={clear}>
              {t(method === "excel" ? "excel.cancel" : "csv.cancel")}
            </button>
            <button
              type="button"
              data-tour="roster-submit"
              className="primary-action"
              disabled={!review.rows.length || Boolean(review.error)}
              onClick={() => void save()}
            >
              {t(saving ? "excel.saving" : method === "excel" ? "excel.confirm" : "submit", {
                count: review.rows.length,
              })}
            </button>
          </div>
        )}
      </fieldset>
    </section>
  );
}
